import { useState } from 'react';
import { ArrowDownLeft, ArrowUpRight, ChevronRight, ChevronUp, HandCoins, TriangleAlert, UserCog } from 'lucide-react';
import SheetModal, { SheetTlacitko } from '../../components/SheetModal';

const fmtKc = (n) => (n == null || isNaN(n)
    ? '—'
    : Math.round(Number(n)).toLocaleString('cs-CZ') + ' Kč');

const fmtHod = (h) => {
    if (h == null || isNaN(h)) return '—';
    const t = Math.round(Number(h) * 60);
    return `${Math.floor(t / 60)} h ${String(t % 60).padStart(2, '0')} min`;
};

const isoNaHHMM = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    return Number.isNaN(d.getTime())
        ? ''
        : `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

/** „1 účtenka", „3 účtenky", „5 účtenek". */
const uctenkyText = (n) => `${n} ${n === 1 ? 'účtenka' : n < 5 ? 'účtenky' : 'účtenek'}`;

const PLATBA = { hotovost: 'hotově', karta: 'kartou', prevod: 'převodem' };

/** Výchozí obsah formuláře úpravy docházky pro daný úkol. */
const vychoziEdit = (t) => ({
    clock_in: isoNaHHMM(t?.mzda?.clock_in),
    clock_out: isoNaHHMM(t?.mzda?.konec),
    sazba_kc_h: t?.mzda?.sazba_kc_h != null ? String(t.mzda.sazba_kc_h) : '',
    prestavka_min: t?.mzda?.prestavka_min != null ? String(t.mzda.prestavka_min) : '',
});

const hhmmNaMinuty = (t) => {
    const m = /^(\d{1,2}):(\d{2})$/.exec((t || '').trim());
    return m ? Number(m[1]) * 60 + Number(m[2]) : null;
};

/** Liší se rozepsaný formulář od toho, co je u úkolu uložené? */
const maZmenu = (t, e) => !!e && (
    e.clock_in !== isoNaHHMM(t?.mzda?.clock_in)
    || e.clock_out !== isoNaHHMM(t?.mzda?.konec)
    || e.sazba_kc_h !== (t?.mzda?.sazba_kc_h != null ? String(t.mzda.sazba_kc_h) : '')
    || e.prestavka_min !== (t?.mzda?.prestavka_min != null ? String(t.mzda.prestavka_min) : '')
);

/**
 * Záloha mzdy a částka k předání pro jedno vyúčtování — **živě podle editace**
 * docházky. Mzda je součástí částky, kterou si ti dva předají; kdyby se
 * přepočítala až na serveru, manažer by potvrzoval jiné číslo, než jaké vidí.
 * Jedna funkce pro kartu pod hlavičkou i pro detail, ať se čísla nerozejdou.
 *
 * @returns {{ zaloha: number, hodiny: number|null, platna: boolean, kOdevzdani: number }}
 *          `kOdevzdani` kladné = manažer peníze přebírá, záporné = vyplácí.
 */
function vypocet(t, e) {
    const jeDohoda = ['DPP', 'DPC'].includes(t?.mzda?.typ_uvazku);
    const mzda = (() => {
        const puvodni = { zaloha: t?.mzda?.zaloha_kc ?? 0, hodiny: t?.mzda?.hodiny ?? null, platna: true };
        if (!e) return puvodni;
        const zac = hhmmNaMinuty(e.clock_in);
        const kon = hhmmNaMinuty(e.clock_out);
        const sazba = parseFloat(e.sazba_kc_h);
        const pauza = parseInt(e.prestavka_min, 10);
        if (zac == null || kon == null || !Number.isFinite(sazba)) return { ...puvodni, platna: false };
        if (kon <= zac) return { ...puvodni, platna: false };
        const hodiny = Math.max(0, (kon - zac - (Number.isFinite(pauza) ? pauza : 0)) / 60);
        return { zaloha: jeDohoda ? Math.round(hodiny * sazba) : 0, hodiny, platna: true };
    })();
    const kOdevzdani = Math.round(((t?.hotovost_predana_kc ?? 0) - (t?.net_kc ?? 0) - mzda.zaloha) * 100) / 100;
    return { ...mzda, kOdevzdani };
}

/**
 * Úkoly manažera směny — dnes jediný druh: potvrdit vyúčtování kolegy.
 *
 * Potvrzení není administrativa, ale **předání peněz**. Manažer si s kolegou
 * u pultu vyrovná hotovost a tímhle tlačítkem řekne, že se to stalo — čímž je
 * vyúčtování schválené. Proto v modalu stojí celý výpočet včetně mzdy:
 * kdo nevidí, jak částka vznikla, nemá co potvrzovat.
 *
 * Notifikace se k tomu neposílá. Karta je jediný kanál — zvonek by pro tutéž
 * jedinou věc přidával druhý a manažer by ji odklikával dvakrát.
 *
 * **Nic se samo neotevírá.** Sheet dřív vyskočil sám, jakmile kolega
 * vyúčtování uzavřel — jenže manažer v tu chvíli dělá něco jiného (markuje,
 * odškrtává, telefonuje) a modal mu to přebil uprostřed pohybu. Teď pod
 * hlavičkou naskočí karta: kdo, kterým směrem a kolik. To je všechno, co
 * potřebuje vědět, aby si kolegu zavolal k pultu; detail s rozpisem
 * a potvrzením otevře klepnutím, až na to má ruce. Bez čekajícího vyúčtování
 * se nevykreslí nic, takže stálá výška obrazovky zůstává.
 */
export default function UkolyManazera({ ukoly = [], onPotvrdit, isDark }) {
    const [otevreno, setOtevreno] = useState(false);
    const [detailId, setDetailId] = useState(null);
    const [reseni, setReseni] = useState(null);
    const [rozpisOtevren, setRozpisOtevren] = useState(false);
    const [editOtevren, setEditOtevren] = useState(false);
    const [edit, setEdit] = useState(null);
    // Ke komu rozepsaná korekce patří. Bez toho ji každé otevření sheetu
    // přepsalo výchozími hodnotami, takže kdo modal omylem zavřel, přišel
    // o ni a nic mu to neřeklo. Do DB se pořád zapisuje až s potvrzením
    // (mzda je součástí předávané částky) — rozepsaný formulář ale nemá
    // důvod mizet dřív, než se o něm rozhodne.
    const [editPro, setEditPro] = useState(null);
    const [busy, setBusy] = useState(false);
    const [chyba, setChyba] = useState(null);

    if (!ukoly.length) return null;

    // Úkol nemusí mít pokladní smenu — vyrovnání bez pokladny (záloha ze
    // mzdy) se pozná podle docházky.
    const klic = (t) => (t.typ === 'zaloha' ? `z${t.dochazka_id}` : `p${t.pokladna_smena_id}`);
    const prvni = ukoly[0];
    const u = ukoly.find((x) => klic(x) === detailId) ?? prvni;
    const jenZaloha = u.typ === 'zaloha';

    const jeDohoda = ['DPP', 'DPC'].includes(u.mzda?.typ_uvazku);
    const { kOdevzdani, ...zivaMzda } = vypocet(u, edit);
    const prebira = kOdevzdani >= 0;
    const castka = Math.abs(kOdevzdani);
    const manko = (u.net_kc ?? 0) < 0;
    const formular = edit ?? vychoziEdit(u);
    const zmeneno = maZmenu(u, edit);

    const potvrdit = async () => {
        if (u.vyzaduje_reseni && !reseni) { setChyba('Vyber, jak se manko vyřeší.'); return; }
        if (!zivaMzda.platna) { setChyba('Zkontroluj zadané časy a sazbu.'); return; }
        setBusy(true); setChyba(null);
        try {
            await onPotvrdit(u, reseni, zmeneno ? edit : null);
            setOtevreno(false); setDetailId(null); setReseni(null);
            setEdit(null); setEditPro(null);
        } catch (e) {
            setChyba(e?.message || 'Potvrzení se nepodařilo.');
        } finally {
            setBusy(false);
        }
    };

    const otevri = (t) => {
        setDetailId(klic(t));
        setReseni(null); setChyba(null);
        setRozpisOtevren(false);
        // Rozepsaná korekce přežije zavření sheetu. Zahodí se jen při
        // přepnutí na jiného kolegu, kde by cizí čísla nedávala smysl.
        const jinyKolega = editPro !== klic(t);
        if (jinyKolega) { setEdit(vychoziEdit(t)); setEditPro(klic(t)); }
        // S rozdělanou korekcí se sekce otevře rovnou — jinak by po návratu
        // byla schovaná a vypadala by jako zahozená.
        setEditOtevren(!jinyKolega && maZmenu(t, edit));
        setOtevreno(true);
    };

    return (
        <>
            {/* Karta pod hlavičkou místo modalu, který dřív vyskočil sám.
                Nese přesně to, podle čeho manažer jedná: kdo, kterým směrem
                a kolik — číslo velké, ať ho přečte i od pokladny. Víc karet
                pod sebe nevrší: dvě plné, zbytek jeden řádek, jinak by
                vyrovnání vytlačilo práci z obrazovky. */}
            <div style={{ flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 8, margin: '0 12px 8px' }}>
                {ukoly.slice(0, 2).map((t) => (
                    <KartaVyrovnani
                        key={klic(t)}
                        ukol={t}
                        vysledek={vypocet(t, editPro === klic(t) ? edit : null)}
                        rozepsano={maZmenu(t, editPro === klic(t) ? edit : null)}
                        onClick={() => otevri(t)}
                        isDark={isDark}
                    />
                ))}
                {ukoly.length > 2 && (
                    <button type="button" onClick={() => otevri(ukoly[2])}
                        style={{
                            display: 'flex', alignItems: 'center', gap: 8,
                            minHeight: 36, padding: '0 12px', borderRadius: 10,
                            background: 'transparent',
                            border: '1px solid rgba(245,158,11,0.35)',
                            color: '#F59E0B', fontSize: 12.5, fontWeight: 600,
                            cursor: 'pointer', font: 'inherit', textAlign: 'left',
                        }}>
                        <span style={{ flex: 1 }}>+ další vyrovnání ({ukoly.length - 2})</span>
                        <ChevronRight size={14} />
                    </button>
                )}
            </div>

            {otevreno && (
                <SheetModal
                    onClose={() => setOtevreno(false)}
                    nadpis="Vyrovnání s kolegou"
                    podnadpis={u.zamestnanec}
                    ikona={HandCoins}
                    barva="#F59E0B"
                    isDark={isDark}
                >
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                        {ukoly.length > 1 && (
                            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                                {ukoly.map((t) => {
                                    const vybran = klic(t) === klic(u);
                                    return (
                                        <button
                                            key={klic(t)}
                                            type="button"
                                            onClick={() => otevri(t)}
                                            style={{
                                                padding: '6px 11px', borderRadius: 999,
                                                fontSize: 12, fontWeight: 700, font: 'inherit',
                                                cursor: 'pointer',
                                                background: vybran ? '#F59E0B' : 'transparent',
                                                color: vybran ? '#1a1205' : 'var(--text-muted)',
                                                border: `1px solid ${vybran ? '#F59E0B' : 'var(--border-default)'}`,
                                            }}>
                                            {t.zamestnanec}
                                        </button>
                                    );
                                })}
                            </div>
                        )}

                        {/* Jádro celé obrazovky: co si ti dva mají u pultu
                            fyzicky předat, a kterým směrem. */}
                        <div style={{
                            padding: '14px 16px', borderRadius: 14,
                            background: prebira
                                ? (isDark ? 'rgba(16,185,129,0.12)' : 'rgba(16,185,129,0.09)')
                                : (isDark ? 'rgba(59,130,246,0.14)' : 'rgba(59,130,246,0.10)'),
                            border: `1px solid ${prebira ? 'rgba(16,185,129,0.45)' : 'rgba(59,130,246,0.45)'}`,
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                                {prebira
                                    ? <ArrowDownLeft size={15} style={{ color: '#10B981' }} />
                                    : <ArrowUpRight size={15} style={{ color: '#3B82F6' }} />}
                                <span style={{
                                    fontSize: 11.5, fontWeight: 700, letterSpacing: '0.04em',
                                    textTransform: 'uppercase',
                                    color: prebira ? '#10B981' : '#3B82F6',
                                }}>
                                    {prebira ? 'Převezmi od kolegy' : 'Vyplať kolegovi'}
                                </span>
                            </div>
                            <div style={{
                                fontSize: 30, fontWeight: 800, marginTop: 4,
                                fontVariantNumeric: 'tabular-nums',
                                color: 'var(--text-primary)',
                            }}>
                                {fmtKc(castka)}
                            </div>
                        </div>

                        {/* Jak ta částka vznikla — tři řádky, které dávají
                            dohromady výsledek výš. */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            {/* Bez pokladny je v hotovosti i dýšku nula — jediné,
                                co se předává, je záloha mzdy. */}
                            {!jenZaloha && ((u.hotovost_predana_kc ?? 0) >= 0
                                ? <Radek label="Hotovost nad základ" value={u.hotovost_predana_kc} isDark={isDark} />
                                : <Radek label="Doplnit základ (platil z tašky)" value={u.hotovost_predana_kc} znamenko isDark={isDark} />)}
                            {/* Manko se v součtu objeví s plusem — kolega ho
                                dorovnává ze svého, takže manažer přebírá víc,
                                než kolik má kolega nad základem. Popisek to
                                musí říct, jinak „Manko +240" vypadá, jako by
                                na tom kolega vydělal. */}
                            {!jenZaloha && (
                                <Radek
                                    label={manko ? 'Dorovnání manka' : 'Dýško kolegovi'}
                                    value={-(u.net_kc ?? 0)}
                                    znamenko
                                    barva={manko ? '#EF4444' : '#10B981'}
                                    isDark={isDark}
                                />
                            )}
                            <Radek
                                label={`Záloha mzdy${zivaMzda.hodiny != null ? ` · ${fmtHod(zivaMzda.hodiny)}` : ''}`}
                                value={-zivaMzda.zaloha}
                                znamenko
                                barva={zmeneno ? '#F59E0B' : undefined}
                                isDark={isDark}
                            />
                            <div style={{
                                display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
                                padding: '10px 12px 0', marginTop: 4,
                                borderTop: '1px solid var(--border-default)',
                            }}>
                                <span style={{ fontSize: 13, fontWeight: 800, color: 'var(--text-primary)' }}>
                                    {prebira ? 'Převezmeš' : 'Vyplatíš'}
                                </span>
                                <span style={{
                                    fontSize: 15, fontWeight: 800, fontVariantNumeric: 'tabular-nums',
                                    color: 'var(--text-primary)',
                                }}>{fmtKc(castka)}</span>
                            </div>
                        </div>

                        {/* Účtenky jdou k manažerovi spolu s penězi — potvrzením
                            je převezme. Samostatné „proplácení" před uzávěrkou
                            odpadlo: u karty se nic nepředávalo a hotovost se
                            jen vracela do tašky, aby šla hned zpátky. Stojí
                            hned pod částkou: peníze a papíry jsou jedno předání. */}
                        {u.uctenky?.length > 0 && (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--text-primary)', padding: '0 2px 5px' }}>
                                    + převezmi {uctenkyText(u.uctenky.length)}
                                </span>
                                {u.uctenky.map((d) => (
                                    <Radek key={d.id}
                                        label={`${d.dodavatel || 'Účtenka'} · ${PLATBA[d.zpusob_platby] || d.zpusob_platby || ''}`}
                                        value={d.castka_s_dph} tlumene isDark={isDark} />
                                ))}
                            </div>
                        )}

                        {u.mzda?.typ_uvazku && !['DPP', 'DPC'].includes(u.mzda.typ_uvazku) && (
                            <div style={{ fontSize: 11.5, color: 'var(--text-muted)', lineHeight: 1.45 }}>
                                {u.mzda.typ_uvazku} — záloha se z pokladny nevyplácí, mzda jde přes účtárnu.
                            </div>
                        )}

                        {/* Úprava docházky patří sem, ne na kolegovu závěrečnou
                            obrazovku. Čas a sazba určují zálohu mzdy a ta je
                            součástí částky, kterou si tu předávají — opravit je
                            jde tedy jen do chvíle, než peníze změní majitele.
                            Potom už by zápis měnil hotovost, co je venku. */}
                        <button
                            type="button"
                            onClick={() => setEditOtevren((v) => !v)}
                            style={{
                                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                padding: '9px 12px', borderRadius: 10, font: 'inherit', cursor: 'pointer',
                                background: zmeneno ? (isDark ? 'rgba(245,158,11,0.12)' : 'rgba(245,158,11,0.09)') : 'transparent',
                                border: `1px solid ${zmeneno ? 'rgba(245,158,11,0.45)' : 'var(--border-default)'}`,
                                color: 'var(--text-secondary)', fontSize: 12.5, fontWeight: 600,
                            }}>
                            <span style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                                <UserCog size={14} style={{ color: zmeneno ? '#F59E0B' : 'var(--text-muted)' }} />
                                {zmeneno ? 'Docházka upravena' : 'Upravit docházku'}
                            </span>
                            <ChevronUp size={14} style={{
                                transform: editOtevren ? 'none' : 'rotate(180deg)',
                                transition: 'transform 0.18s',
                            }} />
                        </button>

                        {editOtevren && (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                <div style={{ display: 'flex', gap: 8 }}>
                                    <Pole label="Začátek" value={formular.clock_in} tip="HH:MM"
                                        onChange={(v) => setEdit((e) => ({ ...(e ?? formular), clock_in: v }))} isDark={isDark} />
                                    <Pole label="Konec" value={formular.clock_out} tip="HH:MM"
                                        onChange={(v) => setEdit((e) => ({ ...(e ?? formular), clock_out: v }))} isDark={isDark} />
                                </div>
                                <div style={{ display: 'flex', gap: 8 }}>
                                    <Pole label="Sazba Kč/h" value={formular.sazba_kc_h} inputMode="decimal"
                                        onChange={(v) => setEdit((e) => ({ ...(e ?? formular), sazba_kc_h: v }))} isDark={isDark} />
                                    <Pole label="Přestávka min" value={formular.prestavka_min} inputMode="numeric"
                                        onChange={(v) => setEdit((e) => ({ ...(e ?? formular), prestavka_min: v }))} isDark={isDark} />
                                </div>
                                {!zivaMzda.platna && (
                                    <div style={{ fontSize: 12, fontWeight: 600, color: '#EF4444' }}>
                                        Konec musí být po začátku a sazba číslo.
                                    </div>
                                )}
                                {!jeDohoda && (
                                    <div style={{ fontSize: 11.5, color: 'var(--text-muted)', lineHeight: 1.45 }}>
                                        {u.mzda?.typ_uvazku || 'Tenhle úvazek'} — záloha se z pokladny nevyplácí,
                                        úprava času se promítne jen do odpracovaných hodin.
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Rozpis tržeb — kdo chce vědět, odkud se hotovost vzala.
                            U vyrovnání bez pokladny není co rozepisovat. */}
                        {!jenZaloha && <button
                            type="button"
                            onClick={() => setRozpisOtevren((v) => !v)}
                            style={{
                                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                padding: '9px 12px', borderRadius: 10, font: 'inherit', cursor: 'pointer',
                                background: 'transparent', border: '1px solid var(--border-default)',
                                color: 'var(--text-secondary)', fontSize: 12.5, fontWeight: 600,
                            }}>
                            <span>Rozpis tržeb a kontroly</span>
                            <ChevronUp size={14} style={{
                                transform: rozpisOtevren ? 'none' : 'rotate(180deg)',
                                transition: 'transform 0.18s',
                            }} />
                        </button>}

                        {rozpisOtevren && !jenZaloha && (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                <Radek label="Tržba hotovost" value={u.trzba_hotovost_kc} isDark={isDark} />
                                <Radek label="Tržba karta" value={u.trzba_karta_kc} isDark={isDark} />
                                <Radek label="Tržba online" value={u.trzba_online_kc} isDark={isDark} />
                                {u.nakupy_hotove_kc > 0 && (
                                    <Radek label="Zaplaceno z tašky (účtenky)" value={-u.nakupy_hotove_kc} znamenko isDark={isDark} />
                                )}
                                <Radek label="Základ v peněžence" value={u.zaklad_kc} tlumene isDark={isDark} />
                                <Radek label="Napočítaná hotovost" value={u.hotovost_v_pen_kc} tlumene isDark={isDark} />
                                <Radek label="Uzávěrka terminálu" value={u.terminal_kc} tlumene isDark={isDark} />
                                <Radek label="Rozdíl hotovost" value={u.dysko_hotovost_kc} znamenko tlumene isDark={isDark} />
                                <Radek label="Rozdíl karta" value={u.dysko_karta_kc} znamenko tlumene isDark={isDark} />
                                <Radek label="Spropitné online" value={u.dysko_online_kc} znamenko tlumene isDark={isDark} />
                            </div>
                        )}

                        {u.rozvozce_komentar && (
                            <div style={{
                                fontSize: 12.5, lineHeight: 1.5, color: 'var(--text-secondary)',
                                padding: '10px 12px', borderRadius: 10,
                                background: isDark ? 'rgba(255,255,255,0.035)' : 'rgba(0,0,0,0.028)',
                            }}>
                                „{u.rozvozce_komentar}"
                            </div>
                        )}

                        {u.vyzaduje_reseni && (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                                    <TriangleAlert size={14} style={{ color: '#EF4444' }} />
                                    <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--text-primary)' }}>
                                        Manko přesahuje {fmtKc(u.limit_auto_kc)} — vyber řešení
                                    </span>
                                </div>
                                {[
                                    { id: 'provozni_naklad', label: 'Provozní náklad', popis: 'Jde na doklad bistra' },
                                    { id: 'srazka_ze_mzdy', label: 'Srážka ze mzdy', popis: 'Vznikne pohledávka za zaměstnancem' },
                                ].map((v) => {
                                    const vybran = reseni === v.id;
                                    return (
                                        <button
                                            key={v.id}
                                            type="button"
                                            onClick={() => { setReseni(v.id); setChyba(null); }}
                                            style={{
                                                display: 'flex', flexDirection: 'column', gap: 2,
                                                padding: '10px 12px', borderRadius: 10,
                                                textAlign: 'left', font: 'inherit', cursor: 'pointer',
                                                background: vybran
                                                    ? (isDark ? 'rgba(239,68,68,0.14)' : 'rgba(239,68,68,0.08)')
                                                    : 'transparent',
                                                border: `1px solid ${vybran ? 'rgba(239,68,68,0.55)' : 'var(--border-default)'}`,
                                            }}>
                                            <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>{v.label}</span>
                                            <span style={{ fontSize: 11.5, color: 'var(--text-muted)' }}>{v.popis}</span>
                                        </button>
                                    );
                                })}
                            </div>
                        )}

                        {chyba && (
                            <div style={{ fontSize: 12.5, fontWeight: 600, color: '#EF4444' }}>{chyba}</div>
                        )}

                        {/* Potvrzení je o penězích, ne o papíru — tak se i jmenuje. */}
                        <SheetTlacitko
                            hlavni
                            isDark={isDark}
                            onClick={potvrdit}
                            disabled={busy || (u.vyzaduje_reseni && !reseni)}
                            barva="#10B981"
                        >
                            <HandCoins size={16} />
                            {busy ? 'Potvrzuji…'
                                : castka > 0
                                    ? (prebira ? `Převzal jsem ${fmtKc(castka)}` : `Vyplatil jsem ${fmtKc(castka)}`)
                                    : 'Potvrdit vyrovnání'}
                        </SheetTlacitko>
                    </div>
                </SheetModal>
            )}
        </>
    );
}

/** Vstupní pole úpravy docházky. */
function Pole({ label, value, onChange, tip, inputMode = 'text', isDark }) {
    return (
        <label style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.03em', color: 'var(--text-muted)', textTransform: 'uppercase' }}>
                {label}
            </span>
            <input
                type="text"
                inputMode={inputMode}
                value={value}
                placeholder={tip}
                onChange={(e) => onChange(e.target.value)}
                style={{
                    width: '100%', boxSizing: 'border-box',
                    padding: '9px 10px', borderRadius: 9,
                    background: isDark ? 'rgba(255,255,255,0.05)' : 'var(--surface-elevated)',
                    border: '1px solid var(--border-default)',
                    color: 'var(--text-primary)',
                    fontSize: 15, fontWeight: 700, fontFamily: 'inherit',
                    fontVariantNumeric: 'tabular-nums',
                }}
            />
        </label>
    );
}

/** Řádek výpočtu. `znamenko` = ukaž i +/−, protože směr je tu informace. */
function Radek({ label, value, znamenko = false, tlumene = false, barva, isDark }) {
    const v = Number(value ?? 0);
    const text = znamenko && v !== 0
        ? (v > 0 ? '+' : '−') + fmtKc(Math.abs(v))
        : fmtKc(Math.abs(v));
    return (
        <div style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
            padding: '7px 12px', borderRadius: 8,
            background: tlumene ? 'transparent' : (isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.022)'),
        }}>
            <span style={{ fontSize: 12.5, color: tlumene ? 'var(--text-muted)' : 'var(--text-secondary)' }}>{label}</span>
            <span style={{
                fontSize: 13, fontWeight: 700, fontVariantNumeric: 'tabular-nums',
                color: barva || (tlumene ? 'var(--text-muted)' : 'var(--text-primary)'),
            }}>{text}</span>
        </div>
    );
}

/**
 * Karta čekajícího vyrovnání pod hlavičkou cockpitu. Oranžový rámeček říká
 * „čeká na tebe", barva směru zelená = převezmeš, modrá = vyplatíš — stejně
 * jako v detailu, ať se oko nemusí učit dvě řeči.
 */
function KartaVyrovnani({ ukol, vysledek, rozepsano, onClick, isDark }) {
    const prebira = vysledek.kOdevzdani >= 0;
    const castka = Math.abs(vysledek.kOdevzdani);
    const barvaSmeru = prebira ? '#10B981' : '#3B82F6';
    const Sipka = prebira ? ArrowDownLeft : ArrowUpRight;
    const tlumene = isDark ? 'rgba(255,255,255,0.6)' : 'var(--text-secondary)';
    const pocetUctenek = ukol.uctenky?.length || 0;
    const poznamky = [
        ukol.vyzaduje_reseni && 'manko — rozhodni v detailu',
        rozepsano && 'rozepsaná korekce docházky',
    ].filter(Boolean);

    return (
        <button type="button" onClick={onClick}
            style={{
                display: 'flex', alignItems: 'center', gap: 12,
                padding: '12px 12px 12px 14px', borderRadius: 14,
                background: isDark
                    ? 'linear-gradient(180deg, rgba(245,158,11,0.16) 0%, rgba(245,158,11,0.07) 100%)'
                    : 'linear-gradient(180deg, rgba(255,247,230,0.98) 0%, rgba(255,240,210,0.8) 100%)',
                border: '1px solid rgba(245,158,11,0.55)',
                boxShadow: isDark
                    ? '0 8px 18px -8px rgba(0,0,0,0.6), inset 0 1px 0 rgba(255,255,255,0.14)'
                    : '0 8px 16px -8px rgba(30,30,50,0.22), inset 0 1px 0 rgba(255,255,255,0.9)',
                cursor: 'pointer', font: 'inherit', textAlign: 'left', width: '100%',
                WebkitTapHighlightColor: 'transparent',
            }}>
            <span style={{
                width: 42, height: 42, borderRadius: 12, flexShrink: 0,
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                background: 'rgba(245,158,11,0.18)', color: '#F59E0B',
            }}>
                <HandCoins size={20} />
            </span>
            <span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 1 }}>
                <span style={{
                    fontSize: 12.5, fontWeight: 600, color: tlumene,
                    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                }}>
                    <b style={{ color: isDark ? '#fff' : 'var(--text-primary)', fontWeight: 700 }}>{ukol.zamestnanec}</b>
                    {' čeká na vyrovnání'}
                </span>
                <span style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
                    <span style={{
                        display: 'inline-flex', alignItems: 'center', gap: 4,
                        fontSize: 12.5, fontWeight: 700, color: barvaSmeru,
                    }}>
                        <Sipka size={13} />
                        {castka === 0 ? 'Bez předání peněz' : (prebira ? 'Převezmi' : 'Vyplať')}
                    </span>
                    {castka > 0 && (
                        <span style={{
                            fontSize: 24, fontWeight: 800, letterSpacing: -0.3,
                            color: isDark ? '#fff' : 'var(--text-primary)',
                            fontVariantNumeric: 'tabular-nums', lineHeight: 1.15,
                        }}>{fmtKc(castka)}</span>
                    )}
                </span>
                {/* Účtenky zvlášť a tlumeně — nejsou problém, jen co si vzít. */}
                {pocetUctenek > 0 && (
                    <span style={{ fontSize: 11.5, fontWeight: 600, color: tlumene }}>
                        + {uctenkyText(pocetUctenek)}
                    </span>
                )}
                {poznamky.length > 0 && (
                    <span style={{
                        display: 'inline-flex', alignItems: 'center', gap: 5,
                        fontSize: 11.5, fontWeight: 600,
                        color: ukol.vyzaduje_reseni ? '#EF4444' : tlumene,
                    }}>
                        {ukol.vyzaduje_reseni && <TriangleAlert size={12} />}
                        {poznamky.join(' · ')}
                    </span>
                )}
            </span>
            <ChevronRight size={18} style={{ color: '#F59E0B', flexShrink: 0 }} />
        </button>
    );
}
